'use client';
import { useEffect, useMemo, useRef } from 'react';
import { ANGLE_LABELS, computeAnglePlacements, mergeAngles } from '@/lib/angles';
import { jointColor, readSkeletonPalette, readStagePalette, type SkeletonPalette, type StagePalette } from '@/lib/theme';
import { PoseData, SKELETON_CONNECTIONS } from '@/lib/types';

interface Props {
  data: PoseData | null;
  videoRef: React.RefObject<HTMLVideoElement | null>;
  cameraOn: boolean;
}

// Unified skeleton palette — reads CSS variables (theme-aware)
const MAJOR_JOINTS = new Set([5, 6, 11, 12, 13, 14]);
const FACE_JOINTS  = new Set([0, 1, 2, 3, 4]);

// Minimum keypoint confidence to render a joint/bone.
// Set low enough that YOLO's output on 640×480 frames still passes.
const KP_CONF_THRESHOLD = 0.15;
const CANVAS_W = 960;
const CANVAS_H = 720;

function parseColorRgb(color: string): [number, number, number] {
  const hex = color.trim();
  if (hex.startsWith('#')) {
    const h = hex.slice(1);
    if (h.length === 3) {
      return [
        parseInt(h[0] + h[0], 16),
        parseInt(h[1] + h[1], 16),
        parseInt(h[2] + h[2], 16),
      ];
    }
    return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
  }
  const m = hex.match(/rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)/);
  if (m) return [Number(m[1]), Number(m[2]), Number(m[3])];
  return [109, 181, 168];
}

// ─────────────────────────────────────────────────────────────────────────────

export function PoseCanvas({ data, videoRef, cameraOn }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const dataRef   = useRef<PoseData | null>(null);
  const cameraOnRef = useRef(cameraOn);

  // Server-camera frame cache
  const imgCache    = useRef<HTMLImageElement | null>(null);

  // SAM2 mask cache
  const maskImgRef  = useRef<HTMLImageElement | null>(null);
  const lastMaskB64 = useRef<string | null>(null);

  // Persistent offscreen canvases for hologram rendering (created once)
  const offMask = useRef<OffscreenCanvas | null>(null);
  const offHolo = useRef<OffscreenCanvas | null>(null);
  const offGlow = useRef<OffscreenCanvas | null>(null);

  useEffect(() => { dataRef.current = data; }, [data]);
  useEffect(() => { cameraOnRef.current = cameraOn; }, [cameraOn]);

  // Decode server camera frame
  useEffect(() => {
    if (!data?.frame) { imgCache.current = null; return; }
    const img = new Image();
    img.onload = () => { imgCache.current = img; };
    img.src = `data:image/jpeg;base64,${data.frame}`;
  }, [data?.frame]);

  // Decode SAM2 mask PNG
  useEffect(() => {
    const m = data?.mask;
    if (!m || m === lastMaskB64.current) return;
    lastMaskB64.current = m;
    const img = new Image();
    img.onload = () => { maskImgRef.current = img; };
    img.src = `data:image/png;base64,${m}`;
  }, [data?.mask]);

  // Create offscreen canvases once
  useEffect(() => {
    offMask.current = new OffscreenCanvas(CANVAS_W, CANVAS_H);
    offHolo.current = new OffscreenCanvas(CANVAS_W, CANVAS_H);
    offGlow.current = new OffscreenCanvas(CANVAS_W, CANVAS_H);
  }, []);

  const anglePlacements = useMemo(() => {
    if (!data?.keypoints?.length) return [];
    const angles = mergeAngles(data.keypoints, data.angles);
    return computeAnglePlacements(angles, data.keypoints, CANVAS_W, CANVAS_H);
  }, [data]);

  // RAF render loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let rafId: number;
    const t0 = performance.now();

    const render = (now: number) => {
      const t     = (now - t0) / 1000;
      const d     = dataRef.current;
      const w     = canvas.width;
      const h     = canvas.height;
      const video = videoRef.current;

      const hasBrowserCam  = cameraOnRef.current &&
                             !!(video && video.readyState >= 2 && !video.paused);
      const hasServerFrame = !!(d?.frame && imgCache.current);
      const hasMask        = !!(maskImgRef.current &&
                                offMask.current && offHolo.current && offGlow.current);

      ctx.clearRect(0, 0, w, h);

      // ── 1. Background ─────────────────────────────────────────────────────
      if (hasBrowserCam) {
        // The <video> element is visible behind this canvas.
        // Only add a dim overlay when the hologram mask is active so the
        // glowing silhouette stands out; otherwise leave the canvas transparent.
        if (hasMask) {
          ctx.fillStyle = 'rgba(0,0,0,0.45)';
          ctx.fillRect(0, 0, w, h);
        }
      } else if (hasServerFrame) {
        ctx.globalAlpha = hasMask ? 0.45 : 0.92;
        ctx.drawImage(imgCache.current!, 0, 0, w, h);
        ctx.globalAlpha = 1;
      } else if (d?.keypoints?.length || d?.demo) {
        drawDemoBackground(ctx, w, h, t, readStagePalette());
      }

      // ── 2. SAM2 hologram effect ───────────────────────────────────────────
      if (hasMask && (hasBrowserCam || hasServerFrame)) {
        drawHologram(
          ctx, maskImgRef.current!, w, h, t,
          offMask.current!, offHolo.current!, offGlow.current!,
        );
      }

      // ── 3. Skeleton + angle labels ────────────────────────────────────────
      if (d?.keypoints?.length) {
        const palette = readSkeletonPalette();
        const angles = mergeAngles(d.keypoints, d.angles);
        drawSkeleton(ctx, d.keypoints, w, h, palette, angles);
      }

      rafId = requestAnimationFrame(render);
    };

    rafId = requestAnimationFrame(render);
    return () => cancelAnimationFrame(rafId);
  }, [videoRef]);

  return (
    <>
      <canvas
        ref={canvasRef}
        width={CANVAS_W}
        height={CANVAS_H}
        className="absolute inset-0 w-full h-full pointer-events-none"
      />
      {anglePlacements.length > 0 && (
        <div className="absolute inset-0 pointer-events-none z-[5]">
          {anglePlacements.map((p) => (
            <div
              key={p.key}
              className="absolute -translate-x-1/2 -translate-y-1/2 px-2 py-0.5 rounded-md text-[11px] font-bold tabular-nums whitespace-nowrap border shadow-[0_2px_10px_rgba(0,0,0,0.6)]"
              style={{
                left: `${(p.labelX / CANVAS_W) * 100}%`,
                top: `${(p.labelY / CANVAS_H) * 100}%`,
                background: 'rgba(8, 8, 10, 0.92)',
                borderColor: 'rgba(109, 181, 168, 0.75)',
                color: '#ffffff',
              }}
            >
              {ANGLE_LABELS[p.key]} {Math.round(p.value)}°
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// ─── Hologram body effect ─────────────────────────────────────────────────────

function drawHologram(
  ctx: CanvasRenderingContext2D,
  maskImg: HTMLImageElement,
  w: number, h: number, t: number,
  offMask: OffscreenCanvas,
  offHolo: OffscreenCanvas,
  offGlow: OffscreenCanvas,
) {
  // 1. Upscale mask to canvas size
  const mCtx = offMask.getContext('2d')!;
  mCtx.clearRect(0, 0, w, h);
  mCtx.drawImage(maskImg, 0, 0, w, h);

  // 2. Hologram body: animated scan bands + gradient base
  const hCtx = offHolo.getContext('2d')!;
  hCtx.clearRect(0, 0, w, h);

  const bodyGrad = hCtx.createLinearGradient(0, 0, 0, h);
  bodyGrad.addColorStop(0,    'rgba(109, 181, 168, 0.16)');
  bodyGrad.addColorStop(0.45, 'rgba(109, 181, 168, 0.08)');
  bodyGrad.addColorStop(1,    'rgba(109, 181, 168, 0.14)');
  hCtx.fillStyle = bodyGrad;
  hCtx.fillRect(0, 0, w, h);

  const bandH   = 55;
  const bandGap = 90;
  const scanOff = (t * 70) % (bandH + bandGap);
  for (let y = -(bandH + bandGap); y < h + bandH; y += bandH + bandGap) {
    const gy = y + scanOff;
    const sg = hCtx.createLinearGradient(0, gy, 0, gy + bandH);
    sg.addColorStop(0,    'rgba(109, 181, 168, 0)');
    sg.addColorStop(0.35, 'rgba(109, 181, 168, 0.18)');
    sg.addColorStop(0.65, 'rgba(109, 181, 168, 0.12)');
    sg.addColorStop(1,    'rgba(109, 181, 168, 0)');
    hCtx.fillStyle = sg;
    hCtx.fillRect(0, gy, w, bandH);
  }

  hCtx.fillStyle = 'rgba(0, 0, 0, 0.18)';
  for (let y = 0; y < h; y += 4) hCtx.fillRect(0, y + 2, w, 2);

  hCtx.globalCompositeOperation = 'destination-in';
  hCtx.drawImage(offMask, 0, 0);
  hCtx.globalCompositeOperation = 'source-over';

  ctx.drawImage(offHolo, 0, 0);

  // 3. Glowing outline
  const gCtx = offGlow.getContext('2d')!;
  gCtx.clearRect(0, 0, w, h);

  const pulse = 0.65 + 0.35 * Math.sin(t * 1.8);
  gCtx.drawImage(offMask, 0, 0);
  gCtx.globalCompositeOperation = 'source-in';
  gCtx.fillStyle = `rgba(109, 181, 168, ${0.45 * pulse})`;
  gCtx.fillRect(0, 0, w, h);
  gCtx.globalCompositeOperation = 'source-over';

  ctx.save();
  ctx.filter = 'blur(20px)';
  ctx.globalCompositeOperation = 'lighter';
  ctx.globalAlpha = 0.45;
  ctx.drawImage(offGlow, 0, 0);

  ctx.filter = 'blur(5px)';
  ctx.globalAlpha = 0.70;
  ctx.drawImage(offGlow, 0, 0);
  ctx.restore();
}

// ─── Demo background ──────────────────────────────────────────────────────────

function drawDemoBackground(
  ctx: CanvasRenderingContext2D,
  w: number, h: number, t: number,
  stage: StagePalette,
) {
  const { secondary: dim } = readSkeletonPalette();
  const bg = ctx.createRadialGradient(w * .5, h * .38, 0, w * .5, h * .5, Math.max(w, h) * .85);
  bg.addColorStop(0, stage.top);
  bg.addColorStop(.6, stage.mid);
  bg.addColorStop(1, stage.bottom);
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, w, h);

  const horizon = h * .76;
  ctx.save();
  ctx.lineCap = 'butt';
  for (let i = 0; i <= 10; i++) {
    const f = (i / 10) ** 1.6, y = horizon + (h - horizon) * f;
    ctx.globalAlpha = f * .18;
    ctx.strokeStyle = stage.grid;
    ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w, y); ctx.stroke();
  }
  for (let i = -10; i <= 10; i++) {
    const endX = w * .5 + (i / 10) * (w * .55);
    ctx.globalAlpha = (1 - Math.abs(i) / 11) * .12;
    ctx.strokeStyle = dim;
    ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(w * .5, horizon); ctx.lineTo(endX, h); ctx.stroke();
  }
  ctx.globalAlpha = 1;
  ctx.restore();

  const sy = (t * 80) % h;
  const sg = ctx.createLinearGradient(0, sy - 30, 0, sy + 30);
  sg.addColorStop(0, 'transparent');
  sg.addColorStop(.5, stage.scan);
  sg.addColorStop(1, 'transparent');
  ctx.fillStyle = sg;
  ctx.fillRect(0, sy - 30, w, 60);

  const vg = ctx.createRadialGradient(w / 2, h / 2, h * .25, w / 2, h / 2, Math.max(w, h) * .75);
  vg.addColorStop(0, 'transparent');
  vg.addColorStop(1, stage.vignette);
  ctx.fillStyle = vg;
  ctx.fillRect(0, 0, w, h);
}

// ─── Skeleton ────────────────────────────────────────────────────────────────

function drawSkeleton(
  ctx: CanvasRenderingContext2D,
  keypoints: [number, number, number][],
  w: number, h: number,
  palette: SkeletonPalette,
  angles: Record<string, number> = {},
) {
  if (!keypoints || keypoints.length < 17) return;
  ctx.save();
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  // Pass 1: soft halo (very subtle)
  ctx.globalCompositeOperation = 'lighter';
  for (const [a, b] of SKELETON_CONNECTIONS) {
    const kpA = keypoints[a], kpB = keypoints[b];
    if (!kpA || !kpB || kpA[2] < KP_CONF_THRESHOLD || kpB[2] < KP_CONF_THRESHOLD) continue;
    const conf = (kpA[2] + kpB[2]) / 2;
    const x1 = kpA[0] * w, y1 = kpA[1] * h, x2 = kpB[0] * w, y2 = kpB[1] * h;
    ctx.strokeStyle = boneGrad(ctx, x1, y1, x2, y2, a, b, palette);
    ctx.lineWidth = 5;
    ctx.globalAlpha = 0.06 * conf;
    ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
  }

  // Pass 2: crisp bone lines
  ctx.globalCompositeOperation = 'source-over';
  for (const [a, b] of SKELETON_CONNECTIONS) {
    const kpA = keypoints[a], kpB = keypoints[b];
    if (!kpA || !kpB || kpA[2] < KP_CONF_THRESHOLD || kpB[2] < KP_CONF_THRESHOLD) continue;
    const conf = (kpA[2] + kpB[2]) / 2;
    const x1 = kpA[0] * w, y1 = kpA[1] * h, x2 = kpB[0] * w, y2 = kpB[1] * h;
    ctx.strokeStyle = boneGrad(ctx, x1, y1, x2, y2, a, b, palette);
    ctx.lineWidth = 1.25;
    ctx.globalAlpha = Math.min(conf * 0.95, 0.92);
    ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
  }
  ctx.globalAlpha = 1;

  // Pass 3: joints — large filled dots for clear visibility
  for (let i = 0; i < keypoints.length; i++) {
    const kp = keypoints[i];
    if (!kp || kp[2] < KP_CONF_THRESHOLD) continue;
    const x = kp[0] * w, y = kp[1] * h, conf = kp[2];
    const color = jointColor(i, palette);
    const [r, g, b] = parseColorRgb(color);

    const isMajor = MAJOR_JOINTS.has(i);
    const isFace  = FACE_JOINTS.has(i);
    const dotR    = isFace ? 4 : isMajor ? 7 : 5.5;
    const ringR   = dotR + (isMajor ? 5 : 3.5);

    // outer soft halo
    ctx.beginPath(); ctx.arc(x, y, ringR, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(${r},${g},${b},${0.12 * conf})`;
    ctx.fill();

    // white border ring
    ctx.beginPath(); ctx.arc(x, y, dotR + 1.2, 0, Math.PI * 2);
    ctx.strokeStyle = `rgba(255,255,255,${0.35 * conf})`;
    ctx.lineWidth = 1.5;
    ctx.stroke();

    // solid dot
    ctx.beginPath(); ctx.arc(x, y, dotR, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(${r},${g},${b},${Math.min(conf * 0.95, 1)})`;
    ctx.fill();

    // inner highlight
    ctx.beginPath(); ctx.arc(x - dotR * 0.2, y - dotR * 0.2, dotR * 0.35, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(255,255,255,${0.22 * conf})`;
    ctx.fill();

    drawJointAngleOnCanvas(ctx, i, x, y, angles);
  }

  drawCompositeAngleOnCanvas(ctx, keypoints, w, h, angles);

  ctx.restore();
}

const JOINT_ANGLE_KEY: Partial<Record<number, string>> = {
  7: 'left_elbow',
  8: 'right_elbow',
  11: 'left_hip',
  12: 'right_hip',
  13: 'left_knee',
  14: 'right_knee',
  15: 'left_ankle_flex',
  16: 'right_ankle_flex',
};

const LEFT_ANGLE_JOINTS = new Set([7, 11, 13, 15]);

function drawJointAngleOnCanvas(
  ctx: CanvasRenderingContext2D,
  jointIdx: number,
  x: number,
  y: number,
  angles: Record<string, number>,
) {
  const key = JOINT_ANGLE_KEY[jointIdx];
  if (!key || angles[key] === undefined) return;

  const text = `${Math.round(angles[key])}°`;
  const offsetX = LEFT_ANGLE_JOINTS.has(jointIdx) ? -52 : 14;
  const tx = x + offsetX;
  const ty = y - 16;

  ctx.save();
  ctx.font = '700 14px system-ui, -apple-system, sans-serif';
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'left';
  ctx.lineWidth = 4;
  ctx.strokeStyle = 'rgba(0, 0, 0, 0.9)';
  ctx.fillStyle = '#ffffff';
  ctx.globalCompositeOperation = 'source-over';
  ctx.globalAlpha = 1;
  ctx.strokeText(text, tx, ty);
  ctx.fillText(text, tx, ty);
  ctx.restore();
}

function drawCompositeAngleOnCanvas(
  ctx: CanvasRenderingContext2D,
  keypoints: [number, number, number][],
  w: number,
  h: number,
  angles: Record<string, number>,
) {
  const ls = keypoints[5], rs = keypoints[6], lh = keypoints[11], rh = keypoints[12];
  const valid = (kp: [number, number, number] | undefined) => !!kp && kp[2] >= KP_CONF_THRESHOLD;

  if (angles.spine !== undefined && valid(ls) && valid(rs) && valid(lh) && valid(rh)) {
    const x = ((ls[0] + rs[0] + lh[0] + rh[0]) / 4) * w;
    const y = ((ls[1] + rs[1] + lh[1] + rh[1]) / 4) * h - 28;
    drawOutlinedText(ctx, `${Math.round(angles.spine)}°`, x - 16, y);
  }

  if (angles.shoulder_tilt !== undefined && valid(ls) && valid(rs)) {
    const x = ((ls[0] + rs[0]) / 2) * w;
    const y = ((ls[1] + rs[1]) / 2) * h - 36;
    drawOutlinedText(ctx, `${Math.round(angles.shoulder_tilt)}°`, x - 16, y);
  }
}

function drawOutlinedText(ctx: CanvasRenderingContext2D, text: string, x: number, y: number) {
  ctx.save();
  ctx.font = '700 14px system-ui, -apple-system, sans-serif';
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'left';
  ctx.lineWidth = 4;
  ctx.strokeStyle = 'rgba(0, 0, 0, 0.9)';
  ctx.fillStyle = '#ffffff';
  ctx.globalCompositeOperation = 'source-over';
  ctx.globalAlpha = 1;
  ctx.strokeText(text, x, y);
  ctx.fillText(text, x, y);
  ctx.restore();
}

function boneGrad(
  ctx: CanvasRenderingContext2D,
  x1: number, y1: number, x2: number, y2: number,
  a: number, b: number,
  palette: SkeletonPalette,
): CanvasGradient {
  const g = ctx.createLinearGradient(x1, y1, x2, y2);
  g.addColorStop(0, jointColor(a, palette));
  g.addColorStop(1, jointColor(b, palette));
  return g;
}
